import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, UserPlus, MessageSquare, X, Check, Clock, Search, Shield, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { teammatesApi } from '../services/teammatesApi';
import ProfileAvatar from '../../profile/components/ProfileAvatar';
import { btn } from '../../utils/buttonStyles';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${Math.floor(diffMonths / 12)}y ago`;
};


export default function TeammatesPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [teammates, setTeammates] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // userId to confirm removal
  const [requestsTab, setRequestsTab] = useState('mine'); // 'mine' | 'managed'

  const [findQuery, setFindQuery] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [teammatesRes, pendingRes, sentRes] = await Promise.all([
        teammatesApi.getTeammates(),
        teammatesApi.getPendingRequests(),
        teammatesApi.getSentRequests(),
      ]);
      const pending = pendingRes?.data || [];
      setTeammates(teammatesRes?.data || []);
      setPendingRequests(pending);
      setSentRequests(sentRes?.data || []);
      // Auto-switch to managed tab if no direct requests but managed ones exist
      const myCount = pending.filter(r => !r.managedPlayer).length;
      const managedCount = pending.filter(r => r.managedPlayer).length;
      if (myCount === 0 && managedCount > 0) {
        setRequestsTab('managed');
      } else {
        setRequestsTab('mine');
      }
    } catch (err) {
      console.error('Failed to load teammates data:', err);
      setError('Failed to load teammates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAccept = async (request) => {
    try {
      const result = await teammatesApi.acceptRequest(request.id);
      // Move from pending to teammates
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      // Only add to teammates list if this is a direct request (not for a managed player)
      if (!request.managedPlayer) {
        const newTeammate = {
          id: result?.id || Date.now(),
          connectedAt: new Date().toISOString(),
          user: request.sender,
        };
        setTeammates(prev => [newTeammate, ...prev]);
      }
    } catch (err) {
      console.error('Failed to accept request:', err);
    }
  };

  const handleDecline = async (request) => {
    try {
      await teammatesApi.declineRequest(request.id);
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err) {
      console.error('Failed to decline request:', err);
    }
  };

  const handleCancel = async (request) => {
    try {
      await teammatesApi.cancelRequest(request.id);
      setSentRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err) {
      console.error('Failed to cancel request:', err);
    }
  };

  const handleRemoveTeammate = async (userId) => {
    try {
      setRemovingId(userId);
      await teammatesApi.removeTeammate(userId);
      setTeammates(prev => prev.filter(t => t.user?.id !== userId));
      setConfirmRemove(null);
    } catch (err) {
      console.error('Failed to remove teammate:', err);
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="text-center">
          <p className={`${theme.textSecondary} mb-4`}>{error}</p>
          <button
            onClick={loadData}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <UserCheck className="w-7 h-7 text-blue-500" />
          <h1 className={`text-2xl font-bold ${theme.text}`}>My Teammates ({teammates.length})</h1>
          <button
            onClick={loadData}
            disabled={loading}
            className={`ml-auto p-2 rounded-full ${theme.cardBackground} ${theme.textSecondary} hover:text-blue-500 transition-colors`}
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin text-blue-500' : ''}`} />
          </button>
        </div>

        {/* Stat Badges */}
        <div className="flex flex-wrap gap-3 mb-8">
          {pendingRequests.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm font-semibold text-green-700">{pendingRequests.length} Incoming</span>
            </div>
          )}
          {sentRequests.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-semibold text-yellow-700">{sentRequests.length} Outgoing</span>
            </div>
          )}
        </div>

        {/* Find People */}
        <section className="mb-8">
          <h2 className={`text-base font-semibold ${theme.textSecondary} uppercase tracking-wide mb-3`}>
            Find People
          </h2>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
            <input
              type="text"
              value={findQuery}
              onChange={(e) => setFindQuery(e.target.value)}
              placeholder="Search teammates by name or email..."
              className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm ${theme.border} ${theme.card} ${theme.text} placeholder:${theme.textMuted} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </section>

        {/* Incoming Requests */}
        {pendingRequests.length > 0 && (() => {
          const myRequests = pendingRequests.filter(r => !r.managedPlayer);
          const managedRequests = pendingRequests.filter(r => r.managedPlayer);
          const showTabs = managedRequests.length > 0;
          const activeRequests = requestsTab === 'managed' ? managedRequests : myRequests;
          return (
            <section className="mb-8">
              <h2 className={`text-base font-semibold ${theme.textSecondary} uppercase tracking-wide mb-3`}>
                Incoming Requests ({pendingRequests.length})
              </h2>

              {/* Tabs - only shown when there are managed requests */}
              {showTabs && (
                <div className={`flex gap-1 p-1 rounded-lg mb-4 w-fit ${theme.backgroundSecondary}`}>
                  <button
                    onClick={() => setRequestsTab('mine')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      requestsTab === 'mine'
                        ? `${theme.card} ${theme.text} ${theme.shadow}`
                        : `${theme.textMuted} hover:${theme.text}`
                    }`}
                  >
                    Mine {myRequests.length > 0 && `(${myRequests.length})`}
                  </button>
                  <button
                    onClick={() => setRequestsTab('managed')}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      requestsTab === 'managed'
                        ? `${theme.card} ${theme.text} ${theme.shadow}`
                        : `${theme.textMuted} hover:${theme.text}`
                    }`}
                  >
                    <Shield className="w-3.5 h-3.5 text-blue-500" />
                    Managed Players {managedRequests.length > 0 && `(${managedRequests.length})`}
                  </button>
                </div>
              )}

              <div className="space-y-3">
                {activeRequests.length === 0 ? (
                  <div className={`${theme.card} ${theme.shadow} rounded-xl p-6 text-center`}>
                    <p className={`text-sm ${theme.textMuted}`}>No {requestsTab === 'managed' ? 'managed player' : ''} requests</p>
                  </div>
                ) : activeRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`${theme.card} ${theme.shadow} rounded-xl p-4 flex items-center gap-4`}
                  >
                    <ProfileAvatar
                      userId={request.sender?.id}
                      profileImage={request.sender?.profileImage || request.sender?.profile_image}
                      firstName={request.sender?.firstName || request.sender?.first_name}
                      lastName={request.sender?.lastName || request.sender?.last_name}
                      size="lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold ${theme.text}`}>
                        {request.sender?.firstName || request.sender?.first_name}{' '}
                        {request.sender?.lastName || request.sender?.last_name}
                      </p>
                      {request.managedPlayer ? (
                        <>
                          <p className={`text-sm ${theme.textMuted}`}>
                            wants to be teammates with{' '}
                            <span className="font-medium text-blue-500">
                              {request.managedPlayer.firstName} {request.managedPlayer.lastName}
                            </span>
                            {' '}· {formatRelativeTime(request.createdAt)}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Shield className="w-3 h-3 text-blue-400" />
                            <span className={`text-xs ${theme.textMuted}`}>Managed player</span>
                          </div>
                        </>
                      ) : (
                        <p className={`text-sm ${theme.textMuted}`}>
                          wants to be your teammate · {formatRelativeTime(request.createdAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleAccept(request)}
                        className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-1`}
                      >
                        <Check className="w-4 h-4" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleDecline(request)}
                        className={`${btn.base} ${btn.sm} ${theme.buttonDanger} flex items-center gap-1`}
                      >
                        <X className="w-4 h-4" />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Outgoing Requests */}
        {sentRequests.length > 0 && (
          <section className="mb-8">
            <h2 className={`text-base font-semibold ${theme.textSecondary} uppercase tracking-wide mb-3`}>
              Outgoing Requests ({sentRequests.length})
            </h2>
            <div className="space-y-3">
              {sentRequests.map((request) => (
                <div
                  key={request.id}
                  className={`${theme.card} ${theme.shadow} rounded-xl p-4 flex items-center gap-4`}
                >
                  <ProfileAvatar
                    userId={request.receiver?.id}
                    profileImage={request.receiver?.profileImage || request.receiver?.profile_image}
                    firstName={request.receiver?.firstName || request.receiver?.first_name}
                    lastName={request.receiver?.lastName || request.receiver?.last_name}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${theme.text}`}>
                      {request.receiver?.firstName || request.receiver?.first_name}{' '}
                      {request.receiver?.lastName || request.receiver?.last_name}
                    </p>
                    <p className={`text-sm ${theme.textMuted}`}>
                      Request sent · {formatRelativeTime(request.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCancel(request)}
                    className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-1`}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* My Teammates */}
        <section>
          {(() => {
            const q = findQuery.trim().toLowerCase();
            const filtered = q
              ? teammates.filter((entry) => {
                  const name = `${entry.user?.firstName || entry.user?.first_name || ''} ${entry.user?.lastName || entry.user?.last_name || ''}`.toLowerCase();
                  const email = (entry.user?.email || '').toLowerCase();
                  return name.includes(q) || email.includes(q);
                })
              : teammates;
            return (
          <>
          <h2 className={`text-base font-semibold ${theme.textSecondary} uppercase tracking-wide mb-3`}>
            My Teammates ({filtered.length}{q ? ` of ${teammates.length}` : ''})
          </h2>
          {teammates.length === 0 ? (
            <div className={`${theme.card} rounded-xl p-10 text-center ${theme.shadow}`}>
              <UserPlus className={`w-12 h-12 mx-auto mb-3 ${theme.textMuted}`} />
              <p className={`font-medium ${theme.text} mb-1`}>No teammates yet</p>
              <p className={`text-sm ${theme.textMuted}`}>
                Use the search above to find players and send a teammate request.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`${theme.card} rounded-xl p-8 text-center ${theme.shadow}`}>
              <p className={`text-sm ${theme.textMuted}`}>No teammates match "{findQuery}"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((entry) => (
                <div
                  key={entry.id}
                  className={`${theme.card} ${theme.shadow} rounded-xl p-4 flex items-center gap-4`}
                >
                  <ProfileAvatar
                    userId={entry.user?.id}
                    profileImage={entry.user?.profileImage || entry.user?.profile_image}
                    firstName={entry.user?.firstName || entry.user?.first_name}
                    lastName={entry.user?.lastName || entry.user?.last_name}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-semibold ${theme.text} hover:underline cursor-pointer`}
                      onClick={() => navigate(`/player/${entry.user?.userHandle || entry.user?.id}`)}
                    >
                      {entry.user?.firstName || entry.user?.first_name}{' '}
                      {entry.user?.lastName || entry.user?.last_name}
                    </p>
                    <p className={`text-sm ${theme.textMuted}`}>
                      Connected {formatRelativeTime(entry.connectedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/messages?with=${entry.user?.id}`)}
                      className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-1`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </button>
                    {confirmRemove === entry.user?.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRemoveTeammate(entry.user?.id)}
                          disabled={removingId === entry.user?.id}
                          className={`${btn.base} ${btn.sm} ${theme.buttonDanger}`}
                        >
                          {removingId === entry.user?.id ? '...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                        >
                          Keep
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemove(entry.user?.id)}
                        className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-1`}
                      >
                        <X className="w-4 h-4" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
            );
          })()}
        </section>
      </div>
    </div>
  );
}
